// ---------------------------------------------------------------------------
// CommentThread — Comment list with CRUD for template review
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { MessageSquare, Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { formatDateTime } from '@/lib/utils';
import { useWorkflowHistory, useAddComment, useEditComment, useDeleteComment } from '@/hooks/use-workflow';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast';

interface CommentThreadProps {
  templateId: string;
}

export function CommentThread({ templateId }: CommentThreadProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: comments, isLoading } = useWorkflowHistory(templateId);
  const addComment = useAddComment(templateId);
  const editComment = useEditComment(templateId);
  const deleteComment = useDeleteComment(templateId);

  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  async function handleAddComment() {
    if (!newComment.trim()) return;
    try {
      await addComment.mutateAsync({ commentText: newComment.trim() });
      setNewComment('');
      toast({ title: 'Comment added', variant: 'success' });
    } catch {
      toast({ title: 'Failed to add comment', variant: 'destructive' });
    }
  }

  async function handleEditComment(commentId: string) {
    if (!editText.trim()) return;
    try {
      await editComment.mutateAsync({ commentId, commentText: editText.trim() });
      setEditingId(null);
      toast({ title: 'Comment updated', variant: 'success' });
    } catch {
      toast({ title: 'Failed to update comment', variant: 'destructive' });
    }
  }

  async function handleDeleteComment(commentId: string) {
    try {
      await deleteComment.mutateAsync(commentId);
      toast({ title: 'Comment deleted', variant: 'success' });
    } catch {
      toast({ title: 'Failed to delete comment', variant: 'destructive' });
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-carbon-700">
        <MessageSquare className="h-4 w-4" /> Comments
      </h3>

      {/* Existing comments */}
      {comments && comments.length > 0 ? (
        <div className="space-y-3">
          {comments.map((c) => {
            const isOwn = c.author_id === user?.userId;
            const isTransition = !!c.workflow_id;
            return (
              <div
                key={c.comment_id}
                className={`rounded-ds-008 border p-3 ${
                  isTransition ? 'bg-silver-50 border-silver-200' : 'bg-white border-silver-200'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    {isTransition ? (
                      <Badge variant="secondary" className="text-[10px]">System</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">{c.author_role}</Badge>
                    )}
                    <span className="text-xs text-carbon-500">
                      {formatDateTime(c.created_on)}
                    </span>
                  </div>
                  {isOwn && !isTransition && editingId !== c.comment_id && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => {
                          setEditingId(c.comment_id);
                          setEditText(c.comment_text);
                        }}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteComment(c.comment_id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>

                {editingId === c.comment_id ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="min-h-[50px]"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleEditComment(c.comment_id)}
                        disabled={editComment.isPending}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-carbon-800">{c.comment_text}</p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-carbon-400 italic">No comments yet.</p>
      )}

      {/* Add new comment */}
      <div className="space-y-2 pt-2 border-t border-silver-200">
        <Textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          className="min-h-[60px]"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-carbon-400">
            {newComment.length}/300
          </span>
          <Button
            size="sm"
            onClick={handleAddComment}
            disabled={!newComment.trim() || addComment.isPending}
          >
            {addComment.isPending ? 'Adding...' : 'Add Comment'}
          </Button>
        </div>
      </div>
    </div>
  );
}
